import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CELL_FIGURE_QUESTION } from "@/payroll/ai-script/ai-script-explanation-chat/selection-context";
import type { AiScriptChatSelectionContext } from "@/payroll/ai-script/types";
import type { RecordRef } from "@/profitability/records/objects";
import type { RecordList } from "@/profitability/records/record-panel";

/** The params a link names a record by: the review email's link to a job. */
const RECORD_PARAMS = ["record", "record_id"] as const;

/**
 * A record and the chat cannot both be open, unless the caller keeps the
 * other. Review passes `holdRecord` so the finding stays selected while chat
 * takes the panel. Leaving Review drops the record.
 * `?record=<dataset>&record_id=<record id>` opens that record.
 */
export const usePageEdge = (holdRecord = false, runId?: string | null) => {
  const [params, setParams] = useSearchParams();
  const linked = params.get("record");
  const linkedId = params.get("record_id");
  const [record, setRecord] = useState<{
    ref: RecordRef;
    list: RecordList | null;
  } | null>(
    linked && linkedId
      ? { ref: { dataset: linked, recordId: linkedId }, list: null }
      : null,
  );
  // Closing the linked record forgets the link, so a refresh does not reopen it.
  const forgetLink = useCallback(() => {
    if (!RECORD_PARAMS.some((key) => params.has(key))) return;
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        RECORD_PARAMS.forEach((key) => updated.delete(key));
        return updated;
      },
      { replace: true },
    );
  }, [params, setParams]);
  const [chat, setChat] = useState<{
    question: string | null;
    runId?: string;
  } | null>(null);
  // A record link followed while the page is open (another job of the same
  // email) opens that record too.
  const link = linked && linkedId ? JSON.stringify([linked, linkedId]) : null;
  const [seenLink, setSeenLink] = useState(link);
  if (link !== seenLink) {
    setSeenLink(link);
    if (linked && linkedId) {
      setChat(null);
      setRecord({ ref: { dataset: linked, recordId: linkedId }, list: null });
    }
  }
  const [selection, setSelection] =
    useState<AiScriptChatSelectionContext | null>(null);
  const openRecord = useCallback(
    (
      ref: RecordRef,
      list?: RecordList | null,
      options?: { keepChat?: boolean },
    ) => {
      if (!options?.keepChat) setChat(null);
      setRecord({ ref, list: list ?? null });
    },
    [],
  );
  const clearEdge = useCallback(() => {
    setChat(null);
    setRecord(null);
    forgetLink();
  }, [forgetLink]);
  const held = useRef(holdRecord);
  const shownRun = useRef(runId);
  useEffect(() => {
    const wasHeld = held.current;
    held.current = holdRecord;
    const changedRun = shownRun.current != null && shownRun.current !== runId;
    shownRun.current = runId;
    if (wasHeld && !holdRecord) clearEdge();
    else if (changedRun) {
      setChat(null);
      setSelection(null);
    }
  }, [holdRecord, runId, clearEdge]);
  const ask = (
    question: string | null,
    options?: { keepRecord?: boolean; runId?: string },
  ) => {
    if (!options?.keepRecord && !holdRecord) setRecord(null);
    setSelection(null);
    setChat({ question, ...(options?.runId ? { runId: options.runId } : {}) });
  };
  return {
    record,
    chat,
    selection,
    openRecord,
    clearEdge,
    closeRecord: () => {
      setRecord(null);
      forgetLink();
    },
    ask,
    askAboutCell: (context: AiScriptChatSelectionContext) => {
      setRecord(null);
      setSelection(context);
      setChat({ question: CELL_FIGURE_QUESTION });
    },
    questionPrepared: () =>
      setChat((current) => current && { ...current, question: null }),
    clearSelection: () => setSelection(null),
    closeChat: () => {
      setChat(null);
      setSelection(null);
    },
  };
};
